import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useClients } from "@/contexts/ClientContext";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { logger } from "@/lib/logger";
import {
  PlugZap, CheckCircle2, AlertCircle, ChevronRight, ExternalLink,
  Facebook, Linkedin, Music2, Image as ImageIcon, FileSpreadsheet,
  Twitter, Mail, Phone, PhoneCall, Briefcase, Receipt, KeyRound, Upload,
  Sparkles, Database, Settings as SettingsIcon, Circle,
} from "lucide-react";

/**
 * Per-client Integrations hub — Configuration-per-provider style.
 *
 * One large hero card per OAuth provider (or API-key family). Each card
 * lists what the OAuth flow covers in plain English and has a single
 * primary "Configuration" button that routes to the provider's dedicated
 * configuration page (where the admin can review connection state, kick
 * off the OAuth round-trip, and assign assets to business units).
 *
 * The per-platform pages still live in the top horizontal tab bar — this
 * hub is the new canonical onboarding surface; those tabs are kept around
 * as deep-links until we're sure nothing else relies on them.
 */

const PROVIDERS = (clientId) => [
  // ============= OAUTH PROVIDERS ======================================
  {
    key: "meta",
    name: "Meta",
    flow: "OAuth 2.0 · Login for Business",
    color: "#1877F2",
    Icon: Facebook,
    covers: "Facebook Ads · Facebook Pages (organic) · Instagram Business · WhatsApp Business",
    blurb: "One Login-for-Business round-trip pulls every Meta asset for this client — paid ads spend, organic posts (with caption/image/stats), Instagram media, and Page demographics.",
    status: "ready",
    configurationPath: `/clients/${clientId}/meta-ads`,
  },
  {
    key: "google",
    name: "Google",
    flow: "OAuth 2.0 · Google",
    color: "#4285F4",
    // Inline multi-coloured G — matches Google's official mark.
    Icon: () => (
      <svg viewBox="0 0 24 24" className="h-7 w-7">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.98.66-2.23 1.06-3.72 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
        <path fill="#FBBC05" d="M5.85 14.11A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.44.35-2.11V7.05H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.95l3.67-2.84z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.67 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
      </svg>
    ),
    covers: "Google Analytics (GA4) · Google Ads · Google Business Profile · YouTube · Search Console · Tag Manager",
    blurb: "Single Google consent grants every Workspace + Marketing API. After consent you map each GA4 property / Ad Account / GBP listing to the correct Business Unit.",
    status: "ready",
    configurationPath: `/clients/${clientId}/google-feeds`,
  },
  {
    key: "microsoft",
    name: "Microsoft",
    flow: "OAuth 2.0 · Microsoft Identity",
    color: "#0078D4",
    Icon: () => <span className="font-bold text-base">MS</span>,
    covers: "Microsoft Advertising (Bing Ads) · Bing Webmaster Tools · Microsoft Clarity",
    blurb: "Microsoft Identity Platform OAuth — single grant covers paid Bing campaigns, organic Bing search performance, and Clarity heatmaps / session recordings.",
    status: "master-only",
    configurationPath: `/clients/${clientId}/microsoft`,
  },
  {
    key: "linkedin",
    name: "LinkedIn",
    flow: "OAuth 2.0 · LinkedIn Marketing",
    color: "#0A66C2",
    Icon: Linkedin,
    covers: "LinkedIn Page (organic posts) · LinkedIn Ads (Sponsored Content + Lead Gen)",
    blurb: "LinkedIn Organization OAuth — separate flow from personal LinkedIn. Requires Marketing Developer Platform approval for the organic + Ads scopes.",
    status: "soon",
    configurationPath: `/clients/${clientId}/linkedin`,
  },
  {
    key: "twitter",
    name: "X (Twitter)",
    flow: "OAuth 2.0 · X (PKCE)",
    color: "#000000",
    Icon: Twitter,
    covers: "X account · organic tweets, impressions, engagements, followers",
    blurb: "X Developer Platform OAuth 2.0 with PKCE. One grant covers organic tweet analytics and (where eligible) Ads spend.",
    status: "master-only",
    configurationPath: `/clients/${clientId}/x`,
  },
  {
    key: "tiktok",
    name: "TikTok",
    flow: "OAuth 2.0 · TikTok for Business",
    color: "#000000",
    Icon: Music2,
    covers: "TikTok Business (organic) · TikTok Ads",
    blurb: "TikTok for Business OAuth — separate from creator OAuth. Single grant covers organic profile/video performance + Ads campaign analytics.",
    status: "ready",
    configurationPath: `/clients/${clientId}/tiktok`,
  },
  {
    key: "pinterest",
    name: "Pinterest",
    flow: "OAuth 2.0 · Pinterest Business",
    color: "#E60023",
    Icon: ImageIcon,
    covers: "Pinterest Business · Pins, boards, organic + Pin Ads",
    blurb: "Pinterest Business Developer Platform OAuth — covers organic Pin performance and Pin Ads in a single grant.",
    status: "master-only",
    configurationPath: `/clients/${clientId}/pinterest`,
  },
  {
    key: "xero",
    name: "Xero",
    flow: "OAuth 2.0 · Xero",
    color: "#13B5EA",
    Icon: Receipt,
    covers: "Xero Accounting · P&L, AR/AP, Invoices, Bills, Contacts",
    blurb: "Xero OAuth at the agency level — discovers every tenant. Admins then map each tenant to a client.",
    status: "master-only",
    configurationPath: `/clients/${clientId}/xero`,
  },

  // ============= API-KEY FAMILIES =====================================
  {
    key: "apikey",
    name: "API key providers",
    flow: "API key · no OAuth",
    color: "#71717A",
    Icon: KeyRound,
    covers: "SendGrid · Twilio · 3CX · KeyPay · OpenAI Ads · Trustpilot · ProductReview",
    blurb: "These providers don't expose OAuth. Each is connected once with an API key on its master settings page and routes per-client automatically.",
    status: "ready",
    secondaries: [
      { label: "SendGrid", to: "/integrations/sendgrid", icon: Mail },
      { label: "Twilio",   to: "/integrations/twilio",   icon: Phone },
      { label: "3CX",      to: "/integrations/3cx",      icon: PhoneCall },
      { label: "KeyPay",   to: "/integrations/keypay",   icon: Briefcase },
      { label: "OpenAI Ads", to: `/clients/${clientId}/openai-ads`, icon: Sparkles },
    ],
  },

  // ============= MANUAL UPLOAD ========================================
  {
    key: "manual",
    name: "Manual · CSV / XLSX",
    flow: "File upload",
    color: "#A1A1AA",
    Icon: FileSpreadsheet,
    covers: "AWStats · ad-hoc CSV / XLSX exports from any platform",
    blurb: "Platforms without an API (or rate-limit-friendly export) can be ingested via the per-client upload wizard. Columns are auto-mapped.",
    status: "ready",
    configurationPath: `/clients/${clientId}/uploads`,
    primaryLabel: "Open upload wizard",
    primaryIcon: Upload,
  },
];

const STATUS_PILL = {
  ready: { label: "Ready · per-client", bg: "bg-emerald-50", text: "text-emerald-800", border: "border-emerald-200", icon: CheckCircle2 },
  "master-only": { label: "Via master only", bg: "bg-blue-50", text: "text-blue-800", border: "border-blue-200", icon: Database },
  soon: { label: "Coming soon", bg: "bg-amber-50", text: "text-amber-800", border: "border-amber-200", icon: AlertCircle },
};

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.soon;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-wider font-mono ${s.bg} ${s.text} ${s.border}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

function ProviderCard({ provider, status }) {
  const navigate = useNavigate();
  const { Icon, color } = provider;
  const PrimaryIcon = provider.primaryIcon || SettingsIcon;
  const primaryLabel = provider.primaryLabel || "Configuration";

  return (
    <Card className="rounded-sm border-zinc-200 hover:border-zinc-400 transition-all overflow-hidden"
      data-testid={`provider-card-${provider.key}`}>
      <div className="h-1.5" style={{ background: color }} />
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          {/* Provider icon */}
          <div className="h-14 w-14 rounded-sm flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}14`, color }}>
            {typeof Icon === "function" && !Icon.displayName && !Icon.render
              ? <Icon />
              : <Icon className="h-7 w-7" />}
          </div>

          {/* Body */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display font-bold text-2xl">{provider.name}</h2>
              <Badge className="rounded-sm bg-zinc-100 text-zinc-700 border-zinc-200 font-mono text-[10px]">
                <KeyRound className="h-3 w-3 mr-1" /> {provider.flow}
              </Badge>
              <StatusPill status={provider.status} />
              {/* Live connection state — only shown for providers we can
                  probe per-client. Master-only providers (Xero, X if
                  master, API-key family) deliberately have no live pill. */}
              <ConnectionPill liveStatus={status} providerKey={provider.key} />
            </div>
            <p className="text-[12px] text-zinc-500 font-mono mt-1">
              <strong className="text-zinc-700 font-sans">Covers:</strong> {provider.covers}
            </p>
            <p className="text-sm text-zinc-700 mt-2 leading-relaxed">{provider.blurb}</p>

            {/* Secondary links — API key family lists individual providers */}
            {provider.secondaries && (
              <div className="mt-4 flex flex-wrap gap-2">
                {provider.secondaries.map((s) => {
                  const SIcon = s.icon;
                  return (
                    <Link key={s.label} to={s.to}
                      data-testid={`provider-secondary-${provider.key}-${s.label.toLowerCase()}`}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 text-[12px] transition-colors">
                      <SIcon className="h-3.5 w-3.5" /> {s.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* Primary CTA — opens the provider's configuration page rather
              than firing the OAuth round-trip immediately. */}
          {provider.configurationPath && (
            <Button onClick={() => navigate(provider.configurationPath)}
              size="lg"
              className="rounded-sm text-white whitespace-nowrap shrink-0"
              style={{ background: color }}
              data-testid={`configuration-${provider.key}`}>
              <PrimaryIcon className="h-4 w-4 mr-2" />
              {primaryLabel}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Live connection-state pill — only rendered for providers we can probe
 * per-client (those in `_PER_CLIENT_INTEGRATION_TABLES` server-side).
 * Returns null for master-only / API-key providers so the surface stays
 * focused on per-client OAuth state as the user requested.
 */
function ConnectionPill({ liveStatus, providerKey }) {
  // Providers that don't have per-client OAuth at all — never show a pill.
  const SKIP = new Set(["apikey", "manual", "xero"]);
  if (SKIP.has(providerKey)) return null;
  if (!liveStatus) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-zinc-200 bg-zinc-50 text-[10px] uppercase tracking-wider font-mono text-zinc-500"
        data-testid={`live-status-${providerKey}-loading`}>
        <Circle className="h-2 w-2 fill-zinc-400 text-zinc-400" /> Checking…
      </span>
    );
  }
  if (liveStatus.connected) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-emerald-200 bg-emerald-50 text-[10px] uppercase tracking-wider font-mono text-emerald-800"
        data-testid={`live-status-${providerKey}-connected`}
        title={liveStatus.scopes ? `Scopes: ${liveStatus.scopes}` : undefined}>
        <CheckCircle2 className="h-3 w-3" />
        {liveStatus.label
          ? <span className="normal-case tracking-normal font-sans">Connected · {liveStatus.label}</span>
          : "Connected"}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm border border-zinc-200 bg-white text-[10px] uppercase tracking-wider font-mono text-zinc-600"
      data-testid={`live-status-${providerKey}-disconnected`}>
      <Circle className="h-2 w-2" /> Not connected
    </span>
  );
}

export default function Connections() {
  const { selected } = useClients();
  const [providerStatus, setProviderStatus] = useState(null);

  // Fetch the per-client connection status whenever the active client changes.
  // We intentionally only run on `client_id` change — the hub is otherwise
  // static between refreshes.
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    api.get(`/clients/${selected.client_id}/integrations/status`)
      .then((r) => { if (!cancelled) setProviderStatus(r.data?.providers || {}); })
      .catch((e) => {
        logger.warn("Failed to load per-client integration status:",
                    e?.response?.data || e);
        // Soft-fail: pills just stay in the "Checking…" state. We don't
        // toast here because this is decorative metadata, not a primary
        // action — pestering the admin every load would be noise.
      });
    return () => { cancelled = true; };
  }, [selected]);

  if (!selected) {
    return (
      <div className="p-6 text-sm text-zinc-500">
        Select a client from the sidebar to see its integrations.
      </div>
    );
  }
  const providers = PROVIDERS(selected.client_id);

  return (
    <div className="space-y-6" data-testid="client-integrations-page">
      {/* Header */}
      <div>
        <div className="label-overline">Connections</div>
        <h1 className="font-display font-extrabold text-4xl tracking-tight">
          Integrations
        </h1>
        <p className="text-sm text-zinc-500 mt-1 max-w-3xl">
          One <em>Configuration</em> button per provider. Each opens the
          dedicated configuration page for <strong>{selected.company_name}</strong>{" "}
          where you can review connection state, kick off the OAuth round-trip,
          and assign assets to business units.
        </p>
      </div>

      {/* Provider cards — one large card per OAuth flow */}
      <div className="space-y-3">
        {providers.map((p) => (
          <ProviderCard key={p.key} provider={p}
            status={providerStatus === null
              ? null  // still loading
              : (providerStatus[p.key] || { connected: false })} />
        ))}
      </div>

      {/* Footer note */}
      <Card className="rounded-sm border-zinc-200 bg-zinc-50/50">
        <CardContent className="p-4 flex items-start gap-3">
          <PlugZap className="h-5 w-5 text-zinc-500 mt-0.5 shrink-0" />
          <div className="text-[12px] text-zinc-600 leading-relaxed">
            The per-platform tabs above (CSV Uploads, Reports, Charts…) still
            link directly into each integration. Once we&apos;re happy with this
            hub as the canonical onboarding surface, those tabs can be slimmed
            down to just the data-consumption screens.{" "}
            <Link to="/integrations/meta/app-review"
              className="text-[#1877F2] underline inline-flex items-center gap-0.5">
              Meta App Review checklist <ExternalLink className="h-3 w-3" />
            </Link>{" "}
            shows live scope-by-scope status for every Meta connection.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
